import { parseMapboxLngLat, parseMapboxLngLatFromRecord } from "../client/geoCoords";
import {
  combinedCountyMapView,
  resolveAssetCountyPoints,
  type AssetCoordinateSource,
  type CountyGeographyPoint,
} from "../geo/countyGeography";
import { countyStateFromLocationLikeRow } from "../client/jurisdictionKeys";
import type { BuxusOperatorPayload } from "../payload/types";
import { assetRowDisplayName } from "./assetScreenHelpers";
import { assetIdAndName, getVisibleAssets, type AssetVisibilityContext } from "./legacyOperatorGates";

export type { AssetCoordinateSource } from "../geo/countyGeography";

export type AssetCatalogEntry = {
  id: string;
  displayName: string;
  countiesLabel: string | null;
  productLabel: string | null;
  center: [number, number] | null;
  zoom: number | null;
  hasCoordinates: boolean;
  coordinateSource: AssetCoordinateSource;
  /** Gazetteer internal points when `coordinateSource === "county"`. */
  countyPoints: readonly CountyGeographyPoint[];
};

function readPath(obj: Record<string, unknown> | null | undefined, key: string): unknown {
  if (!obj) return undefined;
  return obj[key];
}

function parseNumber(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim()) {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function operatorMapCenter(
  payload: BuxusOperatorPayload,
  stakeholderCode: string | null,
): { center: [number, number]; zoom: number } {
  const operator = payload.operator as Record<string, unknown>;
  const stakeholders = operator.stakeholders ?? operator.Stakeholders;
  let stakeholderMap: Record<string, unknown> | null = null;
  if (Array.isArray(stakeholders) && stakeholderCode) {
    for (const row of stakeholders) {
      if (!row || typeof row !== "object") continue;
      const r = row as Record<string, unknown>;
      const code = r.stakeholderCode ?? r.StakeholderCode;
      if (typeof code === "string" && code.trim() === stakeholderCode.trim()) {
        stakeholderMap = r;
        break;
      }
    }
  }

  const rawLat =
    parseNumber(readPath(stakeholderMap, "lat")) ??
    parseNumber(readPath(stakeholderMap, "Lat")) ??
    parseNumber(readPath(operator, "lat")) ??
    parseNumber(readPath(operator, "Lat"));
  const rawLng =
    parseNumber(readPath(stakeholderMap, "long")) ??
    parseNumber(readPath(stakeholderMap, "Long")) ??
    parseNumber(readPath(operator, "long")) ??
    parseNumber(readPath(operator, "Long"));
  const rawZoom =
    parseNumber(readPath(stakeholderMap, "zoomLevel")) ??
    parseNumber(readPath(stakeholderMap, "ZoomLevel")) ??
    parseNumber(readPath(operator, "zoomLevel")) ??
    parseNumber(readPath(operator, "ZoomLevel"));

  const center = parseMapboxLngLat(rawLat, rawLng, { rejectOrigin: true }) ?? [-95.37, 29.76];
  const zoom =
    rawZoom != null && Number.isFinite(rawZoom) ? Math.min(22, Math.max(0, rawZoom)) : 8;
  return { center, zoom };
}

function countiesLabelFromAsset(asset: Record<string, unknown>): string | null {
  const locs =
    asset.assetLocations ??
    asset.AssetLocations ??
    asset.counties ??
    asset.Counties;
  if (!Array.isArray(locs)) return null;
  const parts: string[] = [];
  for (const loc of locs) {
    if (!loc || typeof loc !== "object") continue;
    const cs = countyStateFromLocationLikeRow(loc as Record<string, unknown>);
    if (cs) parts.push(`${cs.county}, ${cs.state}`);
  }
  if (!parts.length) return null;
  return [...new Set(parts)].join(" · ");
}

function productLabelFromAsset(asset: Record<string, unknown>): string | null {
  const raw = asset.productTransported ?? asset.ProductTransported;
  if (Array.isArray(raw)) {
    const parts = raw
      .map((x) => (typeof x === "string" ? x.trim() : ""))
      .filter((x) => x.length > 0);
    return parts.length ? parts.join(", ") : null;
  }
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return null;
}

/** Visible assets with map/list metadata for All Assets hub (web). */
export function buildAssetCatalogEntries(
  payload: BuxusOperatorPayload,
  ctx: AssetVisibilityContext,
): AssetCatalogEntry[] {
  const opFallback = operatorMapCenter(payload, ctx.stakeholderCode ?? null);
  const assets = getVisibleAssets(payload, ctx);
  const out: AssetCatalogEntry[] = [];

  for (const row of assets) {
    const pair = assetIdAndName(row);
    if (!pair) continue;
    const asset = row as Record<string, unknown>;
    const assetCenter = parseMapboxLngLatFromRecord(asset, { rejectOrigin: true });
    const countyPoints = assetCenter ? [] : resolveAssetCountyPoints(asset);
    const countyView = assetCenter ? null : combinedCountyMapView(countyPoints);
    const rawAssetZoom =
      parseNumber(readPath(asset, "zoomLevel")) ?? parseNumber(readPath(asset, "ZoomLevel"));
    const assetZoom =
      rawAssetZoom != null && Number.isFinite(rawAssetZoom)
        ? Math.min(22, Math.max(0, rawAssetZoom))
        : 12;

    let center: [number, number] | null = assetCenter;
    let zoom: number | null = assetCenter ? assetZoom : null;
    let coordinateSource: AssetCoordinateSource = "operator";

    if (assetCenter) {
      coordinateSource = "explicit";
    } else if (countyView) {
      center = countyView.center;
      zoom = countyView.zoom;
      coordinateSource = "county";
    } else {
      center = opFallback.center;
      zoom = opFallback.zoom;
    }

    out.push({
      id: pair.id,
      displayName: assetRowDisplayName(asset),
      countiesLabel: countiesLabelFromAsset(asset),
      productLabel: productLabelFromAsset(asset),
      center,
      zoom,
      hasCoordinates: coordinateSource === "explicit" || coordinateSource === "county",
      coordinateSource,
      countyPoints,
    });
  }

  return out;
}
